import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, isSessionExpired } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { AlertTriangle, CheckCircle2, StopCircle, XCircle } from "lucide-react";
import { Stat } from "./Stat";

export type SummaryShape = {
  job_id: string;
  status: string;
  total: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  retried: number;
  duplicates_detected: number;
  unresolved: number;
  cancel_requested_at: string | null;
  state_history: Array<{ at: string; to: string; from?: string; reason?: string }>;
};

/**
 * Terminal-state summary — loads once from `/api/import/jobs/{id}/summary`,
 * shows totals, cancellation timestamp (if any), and provides "back to
 * imports" + "refresh" actions. Read-only across all roles.
 */
export function TerminalSummary({ jobId, status }: { jobId: string; status: string }) {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<SummaryShape | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<SummaryShape>(`/import/jobs/${jobId}/summary`);
      setSummary(data);
    } catch (e) {
      if (isSessionExpired(e)) return;
      setError(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // Only refetch when the terminal job changes; status is stable per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const started = (summary?.state_history || []).find((h) => h.to === "COMMITTING")?.at;
  const completed = (summary?.state_history || [])
    .slice()
    .reverse()
    .find((h) => ["COMPLETED", "PARTIAL", "FAILED", "CANCELLED"].includes(h.to))?.at;
  const durationSec =
    started && completed
      ? Math.max(
          0,
          Math.round((new Date(completed).getTime() - new Date(started).getTime()) / 1000),
        )
      : null;

  const message =
    status === "COMPLETED"
      ? { icon: CheckCircle2, text: "Import completed successfully.", tone: "ok" as const }
      : status === "PARTIAL"
      ? {
          icon: AlertTriangle,
          text: "Import completed with rows requiring attention.",
          tone: "warn" as const,
        }
      : status === "FAILED"
      ? { icon: XCircle, text: "Import could not complete.", tone: "bad" as const }
      : {
          icon: StopCircle,
          text: "Import was cancelled. Completed mutations remain preserved.",
          tone: "muted" as const,
        };

  if (loading)
    return (
      <div className="text-sm text-slate-500" data-testid="summary-loading">
        Loading summary…
      </div>
    );
  if (error && !summary)
    return (
      <div
        role="alert"
        className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
        data-testid="summary-error"
      >
        Could not load the import summary.{" "}
        <button className="underline" onClick={load}>
          Retry
        </button>
      </div>
    );

  const s = summary!;
  const Icon = message.icon;
  return (
    <div className="space-y-4" data-testid="terminal-summary" data-status={status}>
      <div
        className={`rounded-md border p-3 flex items-start gap-2 text-sm ${
          message.tone === "ok"
            ? "border-emerald-200 bg-emerald-50 text-emerald-900"
            : message.tone === "warn"
            ? "border-amber-200 bg-amber-50 text-amber-900"
            : message.tone === "bad"
            ? "border-rose-200 bg-rose-50 text-rose-900"
            : "border-slate-200 bg-slate-50 text-slate-800"
        }`}
        data-testid="summary-headline"
      >
        <Icon className="w-4 h-4 mt-0.5 shrink-0" />
        <div>{message.text}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label="Total" value={s.total} tone="muted" testId="term-total" />
        <Stat label="Created" value={s.created} tone="ok" testId="term-created" />
        <Stat label="Updated" value={s.updated} tone="ok" testId="term-updated" />
        <Stat label="Skipped" value={s.skipped} tone="muted" testId="term-skipped" />
        <Stat
          label="Failed"
          value={s.failed}
          tone={s.failed ? "bad" : "muted"}
          testId="term-failed"
        />
        <Stat
          label="Unresolved"
          value={s.unresolved}
          tone={s.unresolved ? "warn" : "muted"}
          testId="term-unresolved"
        />
        <Stat label="Retried" value={s.retried} tone="muted" testId="term-retried" />
        <Stat label="Duration" value={durationSec ?? 0} tone="muted" testId="term-duration" />
      </div>

      {s.cancel_requested_at && (
        <div className="text-xs text-slate-600" data-testid="term-cancel-at">
          Cancellation requested at {new Date(s.cancel_requested_at).toLocaleString()}.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Button
          variant="outline"
          className="rounded-full"
          onClick={() => navigate("/imports")}
          data-testid="term-back-to-imports-btn"
        >
          Back to imports
        </Button>
        <Button
          variant="outline"
          className="rounded-full"
          onClick={load}
          data-testid="term-refresh-btn"
        >
          Refresh
        </Button>
      </div>
    </div>
  );
}
